import {
  AlertTriangle,
  Banknote,
  FileText,
  FolderKanban,
  ListChecks,
  PackageMinus,
  ShoppingCart,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import { EmptyState } from '@proerp/ui';
import type { Permission } from '@proerp/shared';
import { formatMoney } from '@/lib/money';
import { usePermissions } from '@/lib/useCurrentUser';
import { useOpenItems } from '@/features/accounting/accountingApi';
import { useMaintenanceSchedules } from '@/features/assembly/assemblyApi';
import { useArticles } from '@/features/inventory/articlesApi';
import { usePurchaseOrders } from '@/features/procurement/procurementApi';
import { useProjects } from '@/features/projects/projectsApi';
import { useProjectTaskSummary } from '@/features/projects/projectTasksApi';
import { useSalesDocumentOverview } from '@/features/sales/salesApi';
import {
  isActiveProject,
  revenueMicrosForYear,
  summarizeOpenQuotes,
} from '../dashboardMetrics';
import { SectionCards, type KpiCardData } from '../SectionCards';

/** Fällig in ≤ 30 Tagen oder bereits überfällig. */
function isDueSoon(nextDueAt: string): boolean {
  return new Date(nextDueAt).getTime() <= Date.now() + 30 * 24 * 60 * 60 * 1000;
}

type GatedKpi = KpiCardData & { permission?: Permission };

/**
 * KPI-Kachelraster. Jede Kachel deklariert eine Berechtigung; finanzielle Kacheln
 * (Umsatz, Offene Posten, Überfällige Rechnungen) verlangen `dashboard.viewfinancials`
 * (ADR 0062), operative Kacheln die jeweilige Modul-Leseberechtigung. Es werden nur
 * die Kacheln gerendert, die der Nutzer sehen darf — nie eine genullte „0,00 €"-Kachel.
 */
export function KpiWidget() {
  const { can } = usePermissions();
  const projects = useProjects({ pageSize: 200 });
  const taskSummary = useProjectTaskSummary();
  const openItems = useOpenItems();
  const orders = usePurchaseOrders();
  const articles = useArticles({ pageSize: 200 });
  const maintenance = useMaintenanceSchedules();
  const salesDocs = useSalesDocumentOverview();

  const projectList = projects.data?.data ?? [];
  const activeProjects = projectList.filter(isActiveProject).length;

  const docList = salesDocs.data?.data ?? [];
  const currentYear = new Date().getFullYear();
  const revenueMicros = revenueMicrosForYear(docList, currentYear);
  const prevRevenueMicros = revenueMicrosForYear(docList, currentYear - 1);
  const revenueDeltaPct =
    prevRevenueMicros > 0n ? Number(((revenueMicros - prevRevenueMicros) * 100n) / prevRevenueMicros) : null;
  const revenueDelta =
    revenueDeltaPct === null
      ? null
      : { text: `${revenueDeltaPct >= 0 ? '+' : ''}${revenueDeltaPct} % ggü. ${currentYear - 1}`, positive: revenueDeltaPct >= 0 };
  const openQuotes = summarizeOpenQuotes(docList);

  const openList = openItems.data?.data ?? [];
  const openTotal = openList.reduce((sum, item) => sum + BigInt(item.open.amount), 0n);
  const overdue = openList.filter((item) => item.isOverdue);

  const openOrders = (orders.data?.data ?? []).filter((order) => ['DRAFT', 'ORDERED'].includes(order.status)).length;

  const lowStock = (articles.data?.data ?? []).filter(
    (article) => article.minStock !== null && article.stockQty < article.minStock,
  ).length;

  const dueMaintenance = (maintenance.data?.data ?? []).filter(
    (schedule) => schedule.isActive && isDueSoon(schedule.nextDueAt),
  ).length;

  const canViewFinancials = can('dashboard.viewfinancials');

  const kpis: GatedKpi[] = [
    {
      permission: 'dashboard.viewfinancials',
      label: `Umsatz ${currentYear}`,
      value: formatMoney({ amount: revenueMicros.toString(), currency: 'EUR' }),
      icon: TrendingUp,
      to: '/verkauf',
      isLoading: salesDocs.isLoading,
      delta: revenueDelta,
      hint: 'fakturiert, laufendes Jahr',
    },
    {
      permission: 'sales.read',
      label: 'Offene Angebote',
      value: String(openQuotes.count),
      icon: FileText,
      to: '/verkauf',
      isLoading: salesDocs.isLoading,
      // Das €-Volumen ist eine Finanzangabe — nur mit dashboard.viewfinancials zeigen.
      hint:
        openQuotes.count > 0 && canViewFinancials
          ? `${formatMoney({ amount: openQuotes.totalGrossMicros.toString(), currency: 'EUR' })} Volumen`
          : openQuotes.count > 0
            ? 'offen'
            : 'keine offen',
    },
    {
      permission: 'projects.read',
      label: 'Aktive Projekte',
      value: String(activeProjects),
      icon: FolderKanban,
      to: '/projekte',
      isLoading: projects.isLoading,
      hint: 'laufend (nicht geschlossen)',
    },
    {
      permission: 'projects.read',
      label: 'Offene Projektaufgaben',
      value: String(taskSummary.data?.data.open ?? 0),
      icon: ListChecks,
      to: '/projekte',
      isLoading: taskSummary.isLoading,
      isAlert: (taskSummary.data?.data.dueSoon ?? 0) > 0,
      hint:
        (taskSummary.data?.data.dueSoon ?? 0) > 0
          ? `${taskSummary.data?.data.dueSoon} fällig in ≤ 7 Tagen (${taskSummary.data?.data.overdue} überfällig)`
          : 'keine kritischen Deadlines',
    },
    {
      permission: 'dashboard.viewfinancials',
      label: 'Offene Posten',
      value: formatMoney({ amount: openTotal.toString(), currency: 'EUR' }),
      icon: Banknote,
      to: '/buchhaltung',
      isLoading: openItems.isLoading,
      hint: `${openList.length} Rechnung${openList.length === 1 ? '' : 'en'} offen`,
    },
    {
      permission: 'dashboard.viewfinancials',
      label: 'Überfällige Rechnungen',
      value: String(overdue.length),
      icon: AlertTriangle,
      to: '/buchhaltung',
      isLoading: openItems.isLoading,
      isAlert: overdue.length > 0,
      hint: overdue.length > 0 ? 'Handlung nötig' : 'alles im Plan',
    },
    {
      permission: 'procurement.read',
      label: 'Offene Bestellungen',
      value: String(openOrders),
      icon: ShoppingCart,
      to: '/beschaffung',
      isLoading: orders.isLoading,
      hint: `${orders.data?.meta.total ?? 0} gesamt`,
    },
    {
      permission: 'assembly.read',
      label: 'Fällige Wartungen',
      value: String(dueMaintenance),
      icon: Wrench,
      to: '/montage',
      isLoading: maintenance.isLoading,
      isAlert: dueMaintenance > 0,
      hint: dueMaintenance > 0 ? '≤ 30 Tage' : 'nichts fällig',
    },
    {
      permission: 'inventory.read',
      label: 'Artikel unter Mindestbestand',
      value: String(lowStock),
      icon: PackageMinus,
      to: '/einkauf',
      isLoading: articles.isLoading,
      isAlert: lowStock > 0,
      hint: lowStock > 0 ? 'Nachschub prüfen' : 'Bestände ok',
    },
  ];

  const visible = kpis.filter((kpi) => !kpi.permission || can(kpi.permission));
  if (visible.length === 0) {
    return <EmptyState icon={FolderKanban} title="Keine Kennzahlen für deine Rolle freigegeben." />;
  }
  return <SectionCards kpis={visible} />;
}
